import { OrbitControls } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import { useEffect, useRef } from "react";
import * as THREE from "three";
import { CameraMode } from "../../hooks/useCameraModeController";

interface CameraTransitionConfig {
  position: [number, number, number];
  target: [number, number, number];
  durationMs: number;
  sequenceId: number;
}

const followPosition = new THREE.Vector3();
const followQuaternion = new THREE.Quaternion();
const handoffForward = new THREE.Vector3();
const handoffProjectedTarget = new THREE.Vector3();
const handoffFallbackTarget = new THREE.Vector3();
const handoffRaycaster = new THREE.Raycaster();
const HANDOFF_RAYCAST_DISTANCE = 5000;
const HANDOFF_MIN_HIT_DISTANCE = 0.05;
type HandoffTargetSource = "raycast" | "previous-raycast" | "fallback";

function computeHandoffTarget(
  camera: THREE.Camera,
  scene: THREE.Scene,
  previousRaycastTarget: THREE.Vector3 | null,
  fallbackTarget: THREE.Vector3,
): { target: THREE.Vector3; source: HandoffTargetSource } {
  camera.getWorldDirection(handoffForward);
  handoffRaycaster.near = 0;
  handoffRaycaster.far = HANDOFF_RAYCAST_DISTANCE;
  handoffRaycaster.set(camera.position, handoffForward);

  const intersections = handoffRaycaster.intersectObjects(scene.children, true);
  const hit = intersections.find(
    (candidate) => candidate.distance > HANDOFF_MIN_HIT_DISTANCE,
  );

  if (hit) {
    return { target: hit.point.clone(), source: "raycast" };
  }

  if (previousRaycastTarget) {
    return {
      target: previousRaycastTarget.clone(),
      source: "previous-raycast",
    };
  }

  return { target: fallbackTarget.clone(), source: "fallback" };
}

function projectTargetOntoViewRay(
  camera: THREE.Camera,
  target: THREE.Vector3,
): THREE.Vector3 {
  camera.getWorldDirection(handoffForward);
  const distance = Math.max(
    camera.position.distanceTo(target),
    HANDOFF_MIN_HIT_DISTANCE,
  );
  return handoffProjectedTarget
    .copy(camera.position)
    .addScaledVector(handoffForward, distance)
    .clone();
}

export function Controls({
  target,
  enabled,
  cameraMode,
  cameraTransition,
  onCameraTransitionComplete,
  followCamera,
  onUserInteractionStart,
  handoffFallbackTarget: fallbackTargetProp,
}: {
  target: [number, number, number];
  enabled: boolean;
  cameraMode: CameraMode;
  cameraTransition?: CameraTransitionConfig | null;
  onCameraTransitionComplete?: () => void;
  followCamera?: THREE.Camera | null;
  onUserInteractionStart?: () => void;
  handoffFallbackTarget?: [number, number, number];
}) {
  const controlsRef = useRef<any>(null);
  const { camera, gl, scene } = useThree();
  const previousCameraModeRef = useRef<CameraMode>(cameraMode);
  const lastValidRaycastTargetRef = useRef<THREE.Vector3 | null>(null);
  const transitionRef = useRef<{
    active: boolean;
    startedAt: number;
    durationMs: number;
    startPosition: THREE.Vector3;
    endPosition: THREE.Vector3;
    startTarget: THREE.Vector3;
    endTarget: THREE.Vector3;
    sequenceId: number;
  } | null>(null);

  useEffect(() => {
    if (!enabled || !controlsRef.current) return;
    controlsRef.current.target.set(target[0], target[1], target[2]);
    controlsRef.current.update();
    controlsRef.current.saveState?.();
  }, [enabled, target]);

  useEffect(() => {
    const domElement = gl?.domElement;
    if (!domElement) return;

    const previousTouchAction = domElement.style.touchAction;
    domElement.style.touchAction = "none";

    return () => {
      domElement.style.touchAction = previousTouchAction;
    };
  }, [gl]);

  useEffect(() => {
    if (!cameraTransition || !controlsRef.current) return;

    transitionRef.current = {
      active: true,
      startedAt: performance.now(),
      durationMs: Math.max(1, cameraTransition.durationMs),
      startPosition: camera.position.clone(),
      endPosition: new THREE.Vector3(...cameraTransition.position),
      startTarget: controlsRef.current.target.clone(),
      endTarget: new THREE.Vector3(...cameraTransition.target),
      sequenceId: cameraTransition.sequenceId,
    };
  }, [camera, cameraMode, cameraTransition]);

  useEffect(() => {
    const previousMode = previousCameraModeRef.current;
    previousCameraModeRef.current = cameraMode;

    if (previousMode !== "follow" || cameraMode !== "user") return;

    try {
      if (followCamera) {
        followCamera.getWorldPosition(followPosition);
        followCamera.getWorldQuaternion(followQuaternion);
        camera.position.copy(followPosition);
        camera.quaternion.copy(followQuaternion);
      }
      camera.updateMatrixWorld(true);

      if (fallbackTargetProp) {
        handoffFallbackTarget.set(
          fallbackTargetProp[0],
          fallbackTargetProp[1],
          fallbackTargetProp[2],
        );
      } else if (controlsRef.current) {
        handoffFallbackTarget.copy(controlsRef.current.target);
      }

      const handoffTarget = computeHandoffTarget(
        camera,
        scene,
        lastValidRaycastTargetRef.current,
        handoffFallbackTarget,
      );
      const appliedHandoffTarget =
        handoffTarget.source === "raycast"
          ? handoffTarget.target
          : projectTargetOntoViewRay(camera, handoffTarget.target);
      if (handoffTarget.source === "raycast") {
        lastValidRaycastTargetRef.current = handoffTarget.target.clone();
      }
      if (controlsRef.current) {
        controlsRef.current.target.copy(appliedHandoffTarget);
        controlsRef.current.update();
        controlsRef.current.saveState?.();
      }
    } catch {
      // ignore OrbitControls internal update errors during follow-to-user handoff
    }
  }, [camera, cameraMode, fallbackTargetProp, followCamera, scene]);

  useFrame(() => {
    if (!controlsRef.current) return;

    if (cameraMode === "follow" && followCamera) {
      followCamera.getWorldPosition(followPosition);
      followCamera.getWorldQuaternion(followQuaternion);

      camera.position.copy(followPosition);
      camera.quaternion.copy(followQuaternion);
      camera.updateMatrixWorld(true);

      if (transitionRef.current?.active) {
        transitionRef.current.active = false;
        transitionRef.current = null;
      }
      return;
    }

    const transition = transitionRef.current;
    if (cameraMode !== "transition" || !transition?.active) {
      return;
    }

    const elapsed = performance.now() - transition.startedAt;
    const t = Math.min(1, elapsed / transition.durationMs);
    const eased = 1 - Math.pow(1 - t, 3);

    camera.position.lerpVectors(
      transition.startPosition,
      transition.endPosition,
      eased,
    );
    controlsRef.current.target.lerpVectors(
      transition.startTarget,
      transition.endTarget,
      eased,
    );
    controlsRef.current.update();

    if (t >= 1) {
      transition.active = false;
      transitionRef.current = null;
      onCameraTransitionComplete?.();
    }
  });

  return (
    <OrbitControls
      ref={controlsRef}
      enabled={enabled && cameraMode === "user"}
      enableZoom
      enablePan
      enableRotate
      enableDamping
      dampingFactor={0.08}
      zoomSpeed={1.2}
      minDistance={0.01}
      maxDistance={5000}
      autoRotate={false}
      touches={{
        ONE: THREE.TOUCH.ROTATE,
        TWO: THREE.TOUCH.DOLLY_PAN,
      }}
      onStart={() => {
        onUserInteractionStart?.();

        if (transitionRef.current?.active) {
          transitionRef.current.active = false;
          transitionRef.current = null;
        }
      }}
      data-oid="i9ryw-f"
    />
  );
}
